/**
 * Created: 2026-03-18
 * Initial commit: Add booking utility functions for slot merging
 *
 * Utilities for formatting and processing booking slot data.
 * Adapted from tc-mini/src/lib/booking.ts for the admin frontend.
 */

export interface BookingSlot {
  courtId: string
  courtName?: string
  startTime: string
  price: number
}

export interface MergedSlot {
  courtId: string
  courtName?: string
  date: string
  startHour: number
  endHour: number
  price: number
}

/**
 * Get weekday text in Chinese format (周一, 周二, etc.)
 */
export function getWeekdayText(date: Date | string): string {
  const d =
    typeof date === 'string'
      ? new Date(`${date}T00:00:00`) // force local time parsing
      : date
  const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
  return weekdays[d.getDay()]
}

/**
 * Merge consecutive booking slots for the same court into time ranges.
 * Slots are consecutive if they share the same courtId, same date, and adjacent hours.
 */
export function mergeConsecutiveSlots(slots: BookingSlot[]): MergedSlot[] {
  if (slots.length === 0) return []

  const toMergeKey = (s: BookingSlot) => {
    const d = new Date(s.startTime)
    const date = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`
    return { courtId: s.courtId, courtName: s.courtName, date, hour: d.getHours() }
  }

  const sorted = [...slots].sort((a, b) => {
    const ka = toMergeKey(a)
    const kb = toMergeKey(b)
    if (ka.courtId !== kb.courtId) return ka.courtId.localeCompare(kb.courtId)
    if (ka.date !== kb.date) return ka.date.localeCompare(kb.date)
    return ka.hour - kb.hour
  })
  const first = toMergeKey(sorted[0])
  const merged: MergedSlot[] = []
  let current: MergedSlot = {
    courtId: first.courtId,
    courtName: first.courtName,
    date: first.date,
    startHour: first.hour,
    endHour: first.hour + 1,
    price: Number(sorted[0].price),
  }

  for (let i = 1; i < sorted.length; i++) {
    const key = toMergeKey(sorted[i])

    if (
      key.courtId === current.courtId &&
      key.date === current.date &&
      key.hour === current.endHour &&
      Number(sorted[i].price) === current.price / (current.endHour - current.startHour)
    ) {
      current.endHour = key.hour + 1
      current.price += Number(sorted[i].price)
    } else {
      merged.push(current)
      current = {
        courtId: key.courtId,
        courtName: key.courtName,
        date: key.date,
        startHour: key.hour,
        endHour: key.hour + 1,
        price: Number(sorted[i].price),
      }
    }
  }

  merged.push(current)
  return merged
}

/**
 * Format merged slots into a display string.
 * e.g. "1号场 2026-03-18 周二 08:00-10:00"
 */
export function formatMergedSlotName(slot: MergedSlot): string {
  const weekday = getWeekdayText(slot.date)
  const startHour = slot.startHour.toString().padStart(2, '0')
  const endHour = slot.endHour.toString().padStart(2, '0')
  return `${slot.courtName ?? slot.courtId} ${slot.date} ${weekday} ${startHour}:00-${endHour}:00`
}
